/**
 * Reads and writes unsent per-chat composer drafts, keyed `chatDraft_<chatId>` (ELARA-952).
 *
 * Storage only. What is worth saving — which attachments survive a restart, when a draft counts as
 * empty — is the composer's call and stays in `ChatInputState.ts`. Holding no such judgement here is
 * what lets `ChatsState` clear a deleted chat's draft without importing the composer.
 */

import type { KeyValueStore } from '#lib/key-value-store.js';

import { z } from 'zod';

import { AnsweredQuestionSchema } from '@elara/domain/models/ChatMessageModelSpec';

const DRAFT_KEY_PREFIX = 'chatDraft_';

/** Web writes are a synchronous `localStorage.setItem`, so typing must not write per keystroke. */
export const DRAFT_SAVE_DEBOUNCE_MS = 500;

function draftKey(chatId: string): string {
  return `${DRAFT_KEY_PREFIX}${chatId}`;
}

/**
 * Maps every chat that currently holds a draft to the trip it belongs to.
 *
 * Needed because deleting a trip archives its chats server-side, and the client stops watching the
 * trip before that happens — so there is no moment where it can observe those chats disappearing and
 * infer which drafts to drop. `KeyValueStore` has no key enumeration either, so without this index a
 * draft whose chat is gone can never be found again. `clearTripChatDrafts` reads it to clear a whole
 * trip's drafts from the trip id alone.
 */
const DRAFT_INDEX_KEY = 'chatDraftIndex';
const DraftIndexSchema = z.record(z.string(), z.string());

function loadDraftIndex(keyValueStore: KeyValueStore): Record<string, string> {
  const storedJson = keyValueStore.get(DRAFT_INDEX_KEY);
  if (!storedJson) return {};
  try {
    const parsed = DraftIndexSchema.safeParse(JSON.parse(storedJson));
    return parsed.success ? parsed.data : {};
  } catch {
    return {};
  }
}

function saveDraftIndex(keyValueStore: KeyValueStore, index: Record<string, string>): void {
  if (Object.keys(index).length === 0) {
    keyValueStore.remove(DRAFT_INDEX_KEY);
    return;
  }
  keyValueStore.set(DRAFT_INDEX_KEY, JSON.stringify(index));
}

const StoredUrlAttachmentSchema = z.object({
  type: z.literal('url'),
  url: z.string(),
  name: z.string(),
  mimeType: z.string(),
  size: z.number().optional(),
});

const StoredDataAttachmentSchema = z.object({
  type: z.string(),
  id: z.string(),
  displayName: z.string().optional(),
  data: z.record(z.string(), z.unknown()).optional(),
});

/** An un-uploaded asset is a local blob reference that cannot outlive a restart, so it never lands here. */
const StoredAttachmentSchema = z.union([StoredUrlAttachmentSchema, StoredDataAttachmentSchema]);

const StoredChatDraftSchema = z.object({
  message: z.string(),
  answeredQuestions: AnsweredQuestionSchema,
  attachments: z.array(StoredAttachmentSchema),
});

export type StoredChatDraft = z.infer<typeof StoredChatDraftSchema>;

export function loadChatDraft(keyValueStore: KeyValueStore, chatId: string): StoredChatDraft | undefined {
  const storedJson = keyValueStore.get(draftKey(chatId));
  if (!storedJson) return undefined;
  try {
    const parsed = StoredChatDraftSchema.safeParse(JSON.parse(storedJson));
    return parsed.success ? parsed.data : undefined;
  } catch {
    // Hand-edited or future-format value — absence, matching the store's never-throws contract.
    return undefined;
  }
}

/**
 * The caller has already filtered the draft to its stored shape and confirmed it is non-empty.
 *
 * `tripId` records the draft in the index so {@link clearTripChatDrafts} can find it later. It is
 * optional because a draft is still worth saving without it — the draft just won't be cleared when its
 * trip is deleted.
 */
export function saveChatDraft(
  keyValueStore: KeyValueStore,
  chatId: string,
  draft: StoredChatDraft,
  tripId?: string,
): void {
  keyValueStore.set(draftKey(chatId), JSON.stringify(draft));
  if (!tripId) return;
  const index = loadDraftIndex(keyValueStore);
  if (index[chatId] === tripId) return;
  index[chatId] = tripId;
  saveDraftIndex(keyValueStore, index);
}

/**
 * Called when the composer is emptied, and when the chat itself is deleted. Deleting a chat is an
 * archive that `restoreChat` can undo, so a restored chat comes back without its draft — intended, the
 * draft belonged to a conversation the user removed.
 */
export function clearChatDraft(keyValueStore: KeyValueStore, chatId: string): void {
  keyValueStore.remove(draftKey(chatId));
  const index = loadDraftIndex(keyValueStore);
  if (!(chatId in index)) return;
  delete index[chatId];
  saveDraftIndex(keyValueStore, index);
}

/**
 * Clears every draft belonging to a trip. Called when the trip is deleted, which archives all its
 * chats — the drafts are unreachable from that point, and this is the last moment the client can act
 * on them.
 */
export function clearTripChatDrafts(keyValueStore: KeyValueStore, tripId: string): void {
  const index = loadDraftIndex(keyValueStore);
  const chatIds = Object.keys(index).filter((chatId) => index[chatId] === tripId);
  if (chatIds.length === 0) return;
  for (const chatId of chatIds) {
    keyValueStore.remove(draftKey(chatId));
    delete index[chatId];
  }
  saveDraftIndex(keyValueStore, index);
}
